import React, {
  memo,
  useEffect,
  useRef,
  useState,
} from '../../../lib/teact/teact';
import { getActions } from '../../../global';

import { useTokenData } from '../../../lib/crypto/CryptoDataStore';
import {
  getFirstCaller,
  getFirstSeen,
  getMentions,
  recordFirstSeen,
  recordMention,
} from '../../../lib/crypto/TokenLedger';
import { type TokenPillMessageContext } from '../../../lib/crypto/TokenPillContext';
import buildClassName from '../../../util/buildClassName';

import useLastCallback from '../../../hooks/useLastCallback';

import Skeleton from '../../ui/placeholder/Skeleton';
import { TokenIcon } from './TokenIcon';

import styles from './TokenPill.module.scss';

type OwnProps = {
  tokenKey: string;
  context?: TokenPillMessageContext;
};

interface LedgerInfo {
  firstSeenMC?: number;
  firstSeenTimestamp?: number;
  firstSeenChatId?: string;
  firstSeenMessageId?: number;
  firstCallerName?: string;
  firstCallerMC?: number;
  firstCallerTimestamp?: number;
  firstCallerChatId?: string;
  firstCallerMessageId?: number;
  mentionCount?: number;
}

const formatCompactRaw = (num?: number) => {
  if (num === undefined) return '-';
  if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return String(num.toFixed(0));
};

const TokenPill = ({ tokenKey, context }: OwnProps) => {
  const data = useTokenData(tokenKey);
  const [ledger, setLedger] = useState<LedgerInfo>({});
  const pillRef = useRef<HTMLSpanElement>();
  const hasTrackedRef = useRef(false);

  // Capture message context at mount time (during synchronous render)
  const contextRef = useRef(context);

  const loadLedgerData = useLastCallback(async () => {
    try {
      const altKey = data.symbol ? `$${data.symbol}` : undefined;
      const ledgerKey = data.mint || tokenKey;
      const candidateAltKey = ledgerKey === tokenKey ? altKey : tokenKey;

      const [firstSeen, firstCaller, mentions] = await Promise.all([
        getFirstSeen(ledgerKey, candidateAltKey),
        getFirstCaller(ledgerKey, candidateAltKey),
        getMentions(ledgerKey, candidateAltKey),
      ]);

      setLedger({
        firstSeenMC: firstSeen?.mcAtSeen,
        firstSeenTimestamp: firstSeen?.timestamp,
        firstSeenChatId: firstSeen?.chatId,
        firstSeenMessageId: firstSeen?.messageId,
        firstCallerName: firstCaller?.senderName || firstCaller?.chatTitle || firstSeen?.senderName
          || firstSeen?.chatTitle || 'Someone',
        firstCallerMC: firstCaller?.mcAtMention || firstSeen?.mcAtSeen,
        firstCallerTimestamp: firstCaller?.timestamp || firstSeen?.timestamp,
        firstCallerChatId: firstCaller?.chatId || firstSeen?.chatId,
        firstCallerMessageId: firstCaller?.messageId || firstSeen?.messageId,
        mentionCount: mentions.length,
      });
    } catch {
      // IndexedDB may not be available
    }
  });

  // Track first-seen via IntersectionObserver (passive — fires when pill scrolls into view)
  useEffect(() => {
    if (!pillRef.current || hasTrackedRef.current || data.isLoading || data.isNotFound) return undefined;

    const observer = new IntersectionObserver(async ([entry]) => {
      if (!entry.isIntersecting || hasTrackedRef.current) return;
      hasTrackedRef.current = true;

      const mc = data.marketCap || 0;
      const price = data.priceUsd || 0;
      if (mc <= 0) return;

      const ctx = contextRef.current;
      const altKey = data.symbol ? `$${data.symbol}` : undefined;
      const ledgerKey = data.mint || tokenKey;
      const candidateAltKey = ledgerKey === tokenKey ? altKey : tokenKey;

      // Record first-seen using mint address and alias so it matches deep search records
      await recordFirstSeen(ledgerKey, mc, price, {
        chatId: ctx?.chatId,
        chatTitle: ctx?.chatTitle,
        senderId: ctx?.senderId,
        senderName: ctx?.senderName,
        messageDate: ctx?.messageDate,
        messageId: ctx?.messageId,
        chain: data.chain,
        mintAddress: data.mint,
        altKey: candidateAltKey,
      });

      // Record this specific mention
      await recordMention(ledgerKey, mc, price, {
        chatId: ctx?.chatId,
        chatTitle: ctx?.chatTitle,
        senderId: ctx?.senderId,
        senderName: ctx?.senderName,
        messageDate: ctx?.messageDate,
        messageId: ctx?.messageId,
        chain: data.chain,
        mintAddress: data.mint,
        altKey: candidateAltKey,
      });

      // Load ledger data for display
      loadLedgerData();

      observer.disconnect();
    }, { threshold: 0.5 });

    observer.observe(pillRef.current);
    return () => observer.disconnect();
  }, [
    data.isLoading,
    data.isNotFound,
    data.marketCap,
    data.priceUsd,
    tokenKey,
    data.symbol,
    data.mint,
    data.chain,
    loadLedgerData,
  ]);

  // Open TopSheet overlay instead of the old popup
  const handleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const { openTokenTopSheet } = getActions();
    openTokenTopSheet({ address: data.mint || tokenKey });
  };

  if (data.isNotFound && !data.hasFetchError) {
    return <span className="plain-text">{tokenKey}</span>;
  }

  // Show "shared @ X MC" on the pill if we have the data
  const sharedMC = ledger.firstSeenMC;
  const currentMC = data.marketCap || 0;

  let pnlPercent: number | undefined;
  if (sharedMC && currentMC > 0) {
    pnlPercent = ((currentMC - sharedMC) / sharedMC) * 100;
  }

  const displayChange = pnlPercent !== undefined ? pnlPercent : data.priceChange24h;
  const displayPositive = (displayChange || 0) >= 0;

  return (
    <span className={styles.pillWrapper} ref={pillRef}>
      <span
        className={buildClassName(styles.pill, data.isLoading && styles.loading)}
        onClick={handleExpand}
      >
        <TokenIcon data={data} className={styles.icon} />
        <span className={styles.ticker}>{data.symbol || tokenKey}</span>
        {data.isLoading && (
          <Skeleton
            variant="rounded-rect"
            animation="wave"
            className={styles.skeletonChange}
            inline
          />
        )}
        {/* Always rendered — opacity-driven fade-in via CSS prevents layout reflow */}
        <span className={buildClassName(styles.change, displayPositive ? styles.positive : styles.negative)}>
          {displayChange !== undefined && !data.isLoading ? (
            <>
              {displayPositive ? '+' : ''}
              {displayChange.toFixed(1)}
              %
            </>
          ) : undefined}
        </span>
        <span className={styles.sharedAt}>
          {sharedMC && !data.isLoading ? (
            <>
              @
              {formatCompactRaw(sharedMC)}
            </>
          ) : undefined}
        </span>
      </span>
    </span>
  );
};

export default memo(TokenPill);
